import { HTTPError } from 'ky';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { getSearchBody } from '../getSearchBody';

import { toDeliveryNoteOption } from './toDeliveryNoteOption';
import { type DeliveryNoteOption } from './types';
import {
  zodDeliveryNoteByIdResponseSchema,
  zodDeliveryNoteSearchResponseSchema,
} from './validations';

const SEARCH_LIMIT = 20;

const zodUuidSchema = z.uuid();

/**
 * Resolves a pasted asset id to a verified picker option via the single-DLN
 * endpoint — the fulltext search column (`st`) does not cover asset ids, so
 * the regular search can never find a UUID. A 404 (unknown id or no access)
 * yields no options rather than an error.
 *
 * @param assetId - The pasted asset id.
 * @param options - Optional request options (e.g., abort signal).
 * @returns The verified option, or an empty list when the DLN is not accessible.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/delivery_note_asset_delivery_note_get
 */
const fetchDeliveryNoteOptionById = async (
  assetId: UUID,
  options?: KyRequestOptions,
): Promise<DeliveryNoteOption[]> => {
  let response: unknown;

  try {
    response = await vestigasApi
      .get(ENDPOINT.DELIVERY_NOTE.GET(assetId), options)
      .json();
  } catch (error) {
    if (error instanceof HTTPError && error.response.status === 404) {
      return [];
    }

    throw error;
  }

  const validated = zodDeliveryNoteByIdResponseSchema.safeParse(response);
  const dlnNumber = validated.success
    ? validated.data.assetState?.body?.header?.id
    : undefined;

  return [{ assetId, dlnNumber: dlnNumber ?? assetId }];
};

/**
 * Lightweight delivery-note lookup for pickers: returns just the asset id
 * and DLN number, skipping the heavy row mapping of `useQueryDeliveryNotes`.
 * A UUID query is resolved via id lookup, everything else via fulltext search.
 *
 * @param query - The fulltext search query or a pasted asset id.
 * @param options - Optional request options (e.g., abort signal).
 * @returns The matching picker options.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/search_delivery_note_asset_delivery_note_search_post
 */
export const fetchDeliveryNoteOptions = async (
  query: string,
  options?: KyRequestOptions,
): Promise<DeliveryNoteOption[]> => {
  if (zodUuidSchema.safeParse(query).success) {
    return fetchDeliveryNoteOptionById(query, options);
  }

  const searchBody = getSearchBody({
    definedCustomFields: [],
    filterGroups: { query },
  });

  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.GET_ALL(), {
      ...options,
      json: {
        ...searchBody,
        limit: SEARCH_LIMIT,
        offset: 0,
        // Same ordering as the regular delivery-note search (newest first) —
        // the backend has proper indexes for dln_date, ad-hoc orders it doesn't.
        orderBy: 'dln_date',
        sortOrder: 'DESC',
      },
    })
    .json();

  const validated = zodDeliveryNoteSearchResponseSchema.safeParse(response);

  if (!validated.success) {
    console.warn(
      '[fetchDeliveryNoteOptions] Response validation failed',
      validated.error,
    );
    return [];
  }

  return (validated.data.assets ?? [])
    .slice(0, SEARCH_LIMIT)
    .map(toDeliveryNoteOption);
};
